/**
 * 专门的建议按钮管理器（去单例化版本）
 * 每个文档有独立的实例，但共享全局事件监听器
 */

import { AcceptIcon, RejectIcon } from './suggestion-icon';

export class SuggestionButtonManager {
  // ✅ 静态变量：全局事件监听器（所有实例共享）
  private static _globalEventListenerSetup = false;
  private static _globalEventListener: ((e: Event) => void) | null = null;

  // ✅ 实例变量：每个实例管理自己的按钮
  private readonly _activeButtons = new Map<string, HTMLElement>();
  private readonly _managerId: string;

  constructor() {
    this._managerId = `BM-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this._setupGlobalEventListener();
    console.log('✅ SuggestionButtonManager 实例创建:', this._managerId);
  }

  /**
   * 🔑 设置全局事件监听器（只设置一次，所有实例共享）
   */
  private _setupGlobalEventListener(): void {
    if (SuggestionButtonManager._globalEventListenerSetup) return;

    // 使用事件委托，监听所有按钮点击
    SuggestionButtonManager._globalEventListener = (e: Event) => {
      const target = e.target as HTMLElement;

      // 检查是否是建议按钮，或者是按钮内的图片
      let buttonElement: HTMLElement | null = null;

      if (target.classList.contains('suggestion-button-apply')) {
        buttonElement = target;
      } else if (
        target.tagName === 'svg' &&
        target.parentElement?.classList.contains('suggestion-button-apply')
      ) {
        // 如果点击的是图片，获取其父元素（按钮）
        buttonElement = target.parentElement;
      }

      if (buttonElement) {
        e.stopPropagation();
        e.preventDefault();

        const action = buttonElement.dataset.action;
        const suggestionId = buttonElement.dataset.suggestionButtons;

        if (action && suggestionId) {
          console.log(`🎯 按钮点击检测: ${action} for ${suggestionId}`);

          // 发送自定义事件
          document.dispatchEvent(
            new CustomEvent('suggestion-action', {
              detail: { suggestionId, action },
              bubbles: true,
            })
          );
        }
      }
    };

    // 🔑 使用事件委托，监听 document 上的所有点击
    document.addEventListener(
      'click',
      SuggestionButtonManager._globalEventListener,
      true
    );
    SuggestionButtonManager._globalEventListenerSetup = true;
    console.log('✅ 全局建议按钮事件监听器已设置（所有实例共享）');
  }

  /**
   * 🔑 创建可操作的建议按钮（修复版本）
   */
  createButton(
    suggestionId: string,
    targetElement: Element
  ): HTMLElement | null {
    if (this._activeButtons.has(suggestionId)) {
      const button = this._activeButtons.get(suggestionId);
      button?.remove();
    }

    const container = document.createElement('div');
    container.className = 'suggestion-button-container';
    container.dataset.suggestionButtons = suggestionId;

    const wrapper = document.createElement('div');
    wrapper.className = 'suggestion-button-container-wrapper';

    const division = document.createElement('div');
    division.className = 'suggestion-button-division';

    // 创建 Accept 按钮
    const acceptButton = document.createElement('button');
    acceptButton.className = 'suggestion-button-apply suggestion-action-accept';
    acceptButton.dataset.action = 'accept';
    acceptButton.dataset.suggestionButtons = suggestionId;
    acceptButton.style.display = 'flex';
    acceptButton.style.alignItems = 'center';
    acceptButton.style.justifyContent = 'center';
    acceptButton.insertAdjacentHTML('beforeend', AcceptIcon);

    // 创建 Reject 按钮
    const rejectButton = document.createElement('button');
    rejectButton.className = 'suggestion-button-apply suggestion-action-reject';
    rejectButton.dataset.action = 'reject';
    rejectButton.dataset.suggestionButtons = suggestionId;
    rejectButton.style.display = 'flex';
    rejectButton.style.alignItems = 'center';
    rejectButton.style.justifyContent = 'center';
    rejectButton.insertAdjacentHTML('beforeend', RejectIcon);

    wrapper.append(rejectButton, division,  acceptButton);
    container.append(wrapper);

    // 定位按钮
    this._positionButton(container, targetElement);

    this._activeButtons.set(suggestionId, container);

    console.log(`✅ 创建可操作按钮: ${suggestionId}`);

    return container;
  }

  /**
   * 移除按钮
   */
  removeButton(suggestionId: string): void {
    const button = this._activeButtons.get(suggestionId);
    if (button) {
      button.remove();
      this._activeButtons.delete(suggestionId);
      console.log(`🧹 移除按钮: ${suggestionId}`);
    }
  }

  /**
   * 移除所有按钮
   */
  removeAllButtons(): void {
    console.log('🧹 移除所有建议按钮');

    this._activeButtons.forEach((button, suggestionId) => {
      button.remove();
      console.log(`🧹 移除按钮: ${suggestionId}`);
    });

    this._activeButtons.clear();

    // 也清理可能遗留的按钮
    document.querySelectorAll('[data-suggestion-buttons]').forEach(button => {
      button.remove();
    });
  }

  /**
   * 获取按钮统计
   */
  getButtonStats(): {
    managed: number;
    total: number;
    suggestions: string[];
  } {
    const totalButtons = document.querySelectorAll(
      '[data-suggestion-buttons]'
    ).length;
    const suggestions = Array.from(
      document.querySelectorAll('[data-suggestion-buttons]')
    )
      .map(el => (el as HTMLElement).dataset.suggestionButtons)
      .filter(Boolean) as string[];

    return {
      managed: this._activeButtons.size,
      total: totalButtons,
      suggestions,
    };
  }

  /**
   * 定位按钮
   */
  private _positionButton(
    container: HTMLElement,
    targetElement: Element
  ): void {
    if (targetElement) {
      targetElement.parentElement?.insertBefore(container, targetElement);
    }
  }

  /**
   * 销毁管理器实例（清理当前实例的按钮）
   */
  destroy(): void {
    this.removeAllButtons();
    console.log('🧹 SuggestionButtonManager 实例已销毁:', this._managerId);
    // 注意：全局事件监听器不清理，因为可能有其他实例还在使用
  }

  /**
   * 静态方法：完全清理（仅在应用卸载时调用）
   */
  static cleanup(): void {
    if (SuggestionButtonManager._globalEventListener) {
      document.removeEventListener(
        'click',
        SuggestionButtonManager._globalEventListener,
        true
      );
      SuggestionButtonManager._globalEventListener = null;
      SuggestionButtonManager._globalEventListenerSetup = false;
      console.log('🧹 全局建议按钮事件监听器已清理');
    }
  }
}
